import { useEffect, useState } from "react";
import { Plan, ProductsFetch, SEGMENTOS_POS } from "@/api/products";
import { useActiveStore } from "@/store/activePages";
import { useProductStore } from "@/store/product";
import { useFormStore } from "@/store/form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { setMarketingInfo } from "@/utils/saleSessionStorage";
import PageLayout from "@/components/PageLayout";
import ProductCard from "@/components/ProductCard";
import { useErrorStore } from "@/store/error";
import ErrorAlert from "@/components/ErrorAlert";
import {
  removeAccentAndSpaceAndSetLowerCase,
  removeAccentsAndSetUpperCase,
} from "@/utils/string";
import { formatPrice } from "@/utils/currency";
import { Loading } from "@/components/Loading";
import { useTagManager } from "@/hooks/useTagManager";

export function Home() {
  const { dataLayer, dataLayerOmega } = useTagManager();
  const [canRender, setCanRender] = useState(false);
  const { data: products, isLoading } = ProductsFetch();
  const [productsToShow, setProductsToShow] = useState<Plan[]>([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const addPage = useActiveStore((store) => store.addPage);
  const setProduct = useProductStore((store) => store.setProduct);

  const addData = useFormStore((store) => store.addData);
  const clearFormStore = useFormStore((store) => store.clearFormStore);
  const clearPages = useActiveStore((store) => store.clearPages);
  const isVisibleModalError = useErrorStore((store) => store.isVisibleModal);

  const handleWindowDataLayers = (product: Plan) => {
    const gbPlan = `${product.gbPadrao}gb`;
    const gbPortability = product.mostrarPortabilidade
      ? `+${product.gbBonusPortabilidade}gb`
      : "";
    const gbPromocional = product.gbPromocional
      ? `+${product.gbPromocional}gb`
      : "";
    const gbDedicated = product.mostrarDedicado
      ? `+${product.gbBonusDedicated}gb`
      : "";
    const totalGbProduct =
      gbPlan + gbPortability + gbDedicated + gbPromocional;

    const price = product.precos[0];
    const fidelity = product.fidelidades[0];
    const fidelityPrice = fidelity?.precos[0];
    const actualFidelityPrice =
      Math.round((price.preco_atual + fidelityPrice?.preco_atual) * 100) / 100;

    const isPos = SEGMENTOS_POS.includes(product.segmento);

    dataLayer({
      event: "step",
      passo: "SELECIONAR-OFERTA",
      sku: removeAccentsAndSetUpperCase(product?.tim_id),
      nome_produto: `${removeAccentAndSpaceAndSetLowerCase(
        product?.nome
      )}-${totalGbProduct}`,
      valor: formatPrice(actualFidelityPrice),
      status: "Sucesso",
    });

    dataLayerOmega({
      ecommerce: {
        event: "add_to_cart",
        currency: "BRL",
        items: [
          {
            item_name: removeAccentAndSpaceAndSetLowerCase(product.nome),
            item_id: product.tim_id,
            price: formatPrice(actualFidelityPrice),
            item_variant: isPos ? "Plano TIM Black" : "Plano TIM Controle",
            quantity: 1,
          },
        ],
      },
    });
  };

  const handleProductSelect = (product: Plan) => {
    clearFormStore();
    clearPages();
    setProduct(product);
    addPage("/personal-data");
    handleWindowDataLayers(product);
  };

  useEffect(() => {
    clearFormStore();
    clearPages();
  }, [clearFormStore, clearPages]);

  useEffect(() => {
    if (products) {
      const filteredProducts = products.filter(
        (plan) =>
          plan.exibir_ecommerce &&
          (plan.segmento === "CONTROLE" || plan.segmento === "POS_PAGO")
      );

      setProductsToShow(filteredProducts);
    }
  }, [products]);

  useEffect(() => {
    if (products) {
      const parsedQuery = { product: "" };
      const relatedProduct = products.find(
        (product) => product.tim_id === parsedQuery.product
      );
      if (relatedProduct) {
        setProduct(relatedProduct);
        addPage("/personal-data");
        navigate("/personal-data");
      } else {
        setCanRender(true);
      }
    }
  }, [products, setProduct, addPage, navigate]);

  useEffect(() => {
    const search = Object.fromEntries(searchParams.entries());

    if (search?.utm_source) {
      setMarketingInfo(search);
    }
    addData(search);
  }, [addData, searchParams]);

  if (isLoading || !canRender) {
    return <Loading src="/tim_logo.svg" />;
  }

  return (
    <PageLayout>
      <div className="mt-4 flex flex-col">
        {productsToShow?.map((product) => (
          <div key={product.nome}>
            <ProductCard
              to="/personal-data"
              product={product}
              onSelect={handleProductSelect}
            />
          </div>
        ))}
      </div>
      {isVisibleModalError && <ErrorAlert />}
    </PageLayout>
  );
}
